import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { updateUserMessage, updateUnreadContact, setReadByForChat, setContactsListenerReady } from '../redux/chat/messageSlice';
import { addContact, fetchContacts } from '../redux/chat/contactSlice';

// Single query listener for all of the current user's chatlogs.
// Fires on ANY change to a matching chatlog doc (new message, unread reset, typing field, etc.).
// Reads lastMessage and unreadCounts directly from snapshot data — no extra Firestore reads.
const useContactsListener = () => {
  const dispatch = useDispatch();
  const uid = useSelector((state) => state.user.uid);
  const contacts = useSelector((state) => state.chat.contacts);
  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;
  const firstSnapshotRef = useRef(true);

  useEffect(() => {
    if (!uid) return;

    const q = query(collection(db, 'chatlogs'), where('memberUids', 'array-contains', uid));

    const unsub = onSnapshot(q, (snap) => {
      snap.docChanges().forEach((change) => {
        if (change.type === 'removed') return;

        const data = change.doc.data();
        const chatId = change.doc.id;

        dispatch(
          updateUserMessage({
            chatId,
            messages: data.lastMessage ? [data.lastMessage] : []
          })
        );

        const unreadCount = data.unreadCounts?.[uid] || 0;
        const contact = data.members?.find((m) => m.uid !== uid);
        if (contact) {
          dispatch(updateUnreadContact({ chatId, contactId: contact.uid, unreadCount }));
        }

        // Sync readBy field — convert Firestore Timestamps to plain seconds for Redux
        if (data.readBy) {
          const readBy = {};
          for (const [memberId, ts] of Object.entries(data.readBy)) {
            readBy[memberId] = ts?.seconds ?? 0;
          }
          dispatch(setReadByForChat({ chatId, readBy }));
        }

        // New chatlog added after initial load = someone started a conversation with this user.
        // Update _contacts so the sidebar shows the new conversation without a page reload.
        if (!firstSnapshotRef.current && change.type === 'added' && contact) {
          dispatch(addContact({
            contactUid: contact.uid,
            chatId,
            name: contact.name || '',
            photoURL: contact.photoURL || '',
            contactJobTitle: contact.contactJobTitle || '',
            job: {}
          }));
        }

        // Chat was deleted by this user but the other side sent a new message.
        // Add the contact immediately from snapshot data so the sidebar updates without reload.
        // fetchContacts runs in background to pick up clearedBefore once the entry is re-created.
        if (!firstSnapshotRef.current && change.type === 'modified' && contact) {
          const unreadCount = data.unreadCounts?.[uid] || 0;
          const alreadyTracked = contactsRef.current.some((c) => c.chatId === chatId);
          if (!alreadyTracked && unreadCount > 0) {
            dispatch(addContact({
              contactUid: contact.uid,
              chatId,
              name: contact.name || '',
              photoURL: contact.photoURL || '',
              contactJobTitle: contact.contactJobTitle || '',
              job: contact.job || {}
            }));
            dispatch(fetchContacts(uid));
          }
        }
      });

      // Signal that the initial snapshot has been delivered — Messages.jsx waits for this
      // before running needsInitialScroll so readByMap/unreadContacts are guaranteed fresh
      if (firstSnapshotRef.current) {
        firstSnapshotRef.current = false;
        dispatch(setContactsListenerReady());
      }
    });

    return () => unsub();
  }, [uid, dispatch]);
};

export default useContactsListener;